import { ReactSelectOption } from '@texas/types';
import { useCallback, useMemo, useState } from 'react';

interface MatrixCellFilterItem {
  matrixNodeXValue?: string | null;
  matrixNodeYValue?: string | null;
}

interface Params<T extends MatrixCellFilterItem> {
  items: T[];
}

const toOptions = (values: string[]): ReactSelectOption[] =>
  Array.from(new Set(values.filter(Boolean)))
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({
      label: value,
      value,
    }));

export function useMatrixCellFilters<T extends MatrixCellFilterItem>({
  items,
}: Params<T>) {
  const [cellX, setCellX] = useState<ReactSelectOption | null>(null);
  const [cellY, setCellY] = useState<ReactSelectOption | null>(null);

  const cellXOptions = useMemo(
    () => toOptions(items.map((item) => item.matrixNodeXValue ?? '')),
    [items],
  );

  const cellYOptions = useMemo(
    () => toOptions(items.map((item) => item.matrixNodeYValue ?? '')),
    [items],
  );

  const filterItems = useCallback(
    <U extends MatrixCellFilterItem>(itemsToFilter: U[]) =>
      itemsToFilter.filter(
        (item) =>
          (!cellX || item.matrixNodeXValue === cellX.value) &&
          (!cellY || item.matrixNodeYValue === cellY.value),
      ),
    [cellX, cellY],
  );

  return {
    cellX,
    cellY,
    setCellX,
    setCellY,
    cellXOptions,
    cellYOptions,
    filterItems,
    hasCellFilters: cellXOptions.length > 0 || cellYOptions.length > 0,
  };
}
